<script setup lang="ts" generic="K extends string | number">
/*
 * 5-step run stepper for the payroll run flow. Each step exposes a status
 * (todo | active | done | blocked). The active step gets a primary fill;
 * done steps get a check; blocked gets an amber border.
 */
import { Check, AlertTriangle } from 'lucide-vue-next';

type Status = 'todo' | 'active' | 'done' | 'blocked';

interface Step<T> {
  key: T;
  label: string;
  status: Status;
}

defineProps<{
  steps: Step<K>[];
  /** Optional: emit when a clickable (done/active) step is clicked. */
  clickable?: boolean;
  /** Tooltip shown on todo (locked) steps. */
  lockedTooltip?: string;
}>();

defineEmits<{ stepClick: [key: K] }>();

const cls = {
  todo: 'border-app-border bg-app-surface text-app-muted',
  active: 'border-primary bg-primary text-white',
  done: 'border-primary bg-primary-50 text-primary-700',
  blocked: 'border-amber-300 bg-amber-50 text-amber-800',
} as const;
</script>

<template>
  <ol class="flex w-full items-center gap-2">
    <li v-for="(s, idx) in steps" :key="String(s.key)" class="flex flex-1 items-center gap-2">
      <button
        type="button"
        :disabled="!clickable || s.status === 'todo'"
        :class="[
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-md font-semibold transition-colors',
          cls[s.status],
          clickable && s.status !== 'todo' ? 'cursor-pointer' : 'cursor-default',
        ]"
        :aria-current="s.status === 'active' ? 'step' : undefined"
        :title="s.status === 'todo' && lockedTooltip ? lockedTooltip : undefined"
        @click="clickable && s.status !== 'todo' && $emit('stepClick', s.key)"
      >
        <Check v-if="s.status === 'done'" class="h-4 w-4" aria-hidden="true" />
        <AlertTriangle v-else-if="s.status === 'blocked'" class="h-4 w-4" aria-hidden="true" />
        <span v-else>{{ idx + 1 }}</span>
      </button>
      <span
        :class="[
          'text-md font-medium whitespace-nowrap',
          s.status === 'active'
            ? 'text-app-fg'
            : s.status === 'done'
              ? 'text-primary-700'
              : s.status === 'blocked'
                ? 'text-amber-800'
                : 'text-app-muted',
        ]"
      >
        {{ s.label }}
      </span>
      <span
        v-if="idx < steps.length - 1"
        :class="['mx-2 h-px flex-1', s.status === 'done' ? 'bg-primary' : 'bg-app-border']"
        aria-hidden="true"
      />
    </li>
  </ol>
</template>
